import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import axios from "axios";
import { api } from "../../../utils/api";
import Sidebar from "../sidebar";
import ActionButtons from "../InitialKickoff/MaterialTopics/ActionButtons";
import { companyId } from "../../../utils/companyId";
import InputCard from "./InputCard";
import { numberError, percentageError } from "../../../utils/validationMessage";
import {
  validateNumber,
  validatePercentage,
} from "../../../utils/validateFields";
import { ProgressContext } from "../ContextAPI";
import SkipQuestionare from "../../Common/SkipQuestionare";
import InitialQuestionareSidebar from "../../../Layout/InitialQuestionareSidebar";
import { isFilled, isFilledValue } from "../../../utils/common";
import { getQuestionareSubmitted } from "../../../utils/storage";

const WasteManagement = () => {
  const navigate = useNavigate();
  const [isExist, setIsExist] = useState(false);
  const [formData, setFormData] = useState({
    totalWasteGenerated: { value: "" },
    wasteIntensity: { value: "" },
    wasteDivertedFromLandFill: { value: "" },
    hazardousWasteManagement: { value: "" },
    companyId,
  });
  const { setUpdate } = useContext(ProgressContext);
  const companyDetails = getQuestionareSubmitted();

  const [errors, setErrors] = useState({
    totalWasteGenerated: "",
    wasteIntensity: "",
    wasteDivertedFromLandFill: "",
    hazardousWasteManagement: "",
  });

  // Waste data for creating input cards
  const wasteData = [
    {
      title: "Total Waste Generated",
      placeholder: "1000",
      name: "totalWasteGenerated",
      adornmentText: "Metric Tons",
    },
    {
      title: "Waste Intensity",
      placeholder: "30",
      name: "wasteIntensity",
      adornmentText: "Waste per unit of output",
    },
    {
      title: "Waste Diverted from Landfill",
      placeholder: "10",
      name: "wasteDivertedFromLandFill",
      adornmentText: "% of total waste",
    },
    {
      title: "Hazardous Waste Management",
      placeholder: "1000",
      name: "hazardousWasteManagement",
      adornmentText: "Metric Tons",
    },
  ];

  // Navigating back
  const Back = () => {
    navigate("/environment/water-management");
  };

  const validateForm = () => {
    const {
      totalWasteGenerated,
      wasteIntensity,
      wasteDivertedFromLandFill,
      hazardousWasteManagement,
    } = formData;

    let newErrors = {};

    if (
      totalWasteGenerated.value !== "" &&
      !validateNumber(totalWasteGenerated.value)
    ) {
      newErrors.totalWasteGenerated = numberError;
    }

    if (wasteIntensity.value !== "" && !validateNumber(wasteIntensity.value)) {
      newErrors.wasteIntensity = numberError;
    }

    if (
      wasteDivertedFromLandFill.value !== "" &&
      !validatePercentage(wasteDivertedFromLandFill.value)
    ) {
      newErrors.wasteDivertedFromLandFill = percentageError;
    }

    if (
      hazardousWasteManagement.value !== "" &&
      !validateNumber(hazardousWasteManagement.value)
    ) {
      newErrors.hazardousWasteManagement = numberError;
    }

    setErrors(newErrors);

    // Return true if there are no errors
    return Object.keys(newErrors).length === 0;
  };

  // Fetching data if already exists
  useEffect(() => {
    setUpdate("ESG");
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${api}/environment/waste-management/${companyId}`
        );

        setFormData(response.data.wasteData);
        setIsExist(true);
      } catch (err) {
        console.error("Error:", err);
      }
    };
    fetchData();
  }, []);

  // Save the formData and make the post request
  const Save = async () => {
    if (!validateForm()) {
      return;
    }
    try {
      const {
        totalWasteGenerated,
        wasteIntensity,
        wasteDivertedFromLandFill,
        hazardousWasteManagement,
      } = formData;

      // ✅ Determine waste_management flag
      const waste_management =
        isFilledValue(totalWasteGenerated) &&
        isFilledValue(wasteIntensity) &&
        isFilledValue(wasteDivertedFromLandFill) &&
        isFilledValue(hazardousWasteManagement);

      // ✅ Final payload
      const payload = {
        ...formData,
        companyId,
        waste_management,
      };
      await axios({
        method: isExist ? "put" : "post",
        url: `${api}/environment/waste-management${
          isExist ? `/${companyId}` : ""
        }`,
        data: payload,
      });

      navigate(
        companyDetails?.questionareSubmitted
          ? "/preview"
          : "/environment/biodiversity"
      );
    } catch (err) {
      alert(err.response.data.message);
    }
  };

  // Handling the input value
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: {
        ...prevFormData[name],
        value: value,
      },
    }));
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" })); // Clear error on change
  };

  return (
    <Box sx={{ display: "flex", backgroundColor: "#EDEDED", height: "100vh" }}>
      <Box
        sx={{
          width: "25vw",
          // overflowY: "auto",
        }}
      >
        <InitialQuestionareSidebar />
      </Box>
      <Box
        sx={{
          p: 2,
          mt: 3,
          width: "75vw",
          height: "100vh", // Full viewport height
          overflowY: "auto", // Allow vertical scrolling if content exceeds viewport
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start", // Adjust to start at the top of the box
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "80%",
            marginBottom: "1rem",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              zIndex: 1,
              padding: "10px 0px",
              borderRadius: "4px",
              alignSelf: "flex-start",
            }}
          >
            ENVIRONMENT: WASTE MANAGEMENT
          </Typography>
          <SkipQuestionare />
        </div>
        <Box sx={{ mt: 3, width: "80%" }}>
          {wasteData.map((item, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <InputCard
                key={index}
                title={item.title}
                placeholder={item.placeholder}
                name={item.name}
                value={formData[item.name].value}
                handleChange={handleChange}
                adornmentText={item.adornmentText}
                borderColor={errors[item.name] && "#D34040"}
                error={errors[item.name]} // Pass the error message
              />
              {errors[item.name] && (
                <Typography variant="body2" color="error" sx={{ mt: 0 }}>
                  {errors[item.name]}
                </Typography>
              )}
            </Box>
          ))}
          {/* Back, Save Buttons */}
          <ActionButtons
            Back={Back}
            Move={Save}
            disable={companyDetails?.questionareSubmitted}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default WasteManagement;
